/** The flows `<baluu-booking>` shows, one per address of the school's site
 * (plans/websitev2-parity-m3-booking-management.md):
 * - manage: /bookings/manage/<booking-id>
 * - cancel: /bookings/cancel/<booking-id>
 * - reschedule: /bookings/reschedule/<booking-id>
 * - review: /bookings/review/<booking-id>
 * - bundle-dates: /bookings/bundle/<booking-id> (a bundle booking's dates)
 * - bundle-manage: /bundles/manage/<bundle-id>
 * - flexitime-confirm: /bookings/<booking-id>/<verb>
 * - flexitime-reschedule: /bookings/<booking-id>/<verb>/<date>
 * and the payment pages (plans/websitev2-parity-m4-payment-links.md), each
 * keyed by `record-id`:
 * - secure-payment: /secure/payment/<order-id>[/<instalment>] (a payment link)
 * - secure-pay-booking: /secure/pay/booking/<payment-id>
 * - request-proposal: /secure/request/<proposal-id> (a request-to-book offer)
 * - payment-success: /payment/success/<booking-id>
 * - product-success: /payment/product/success/<purchase-id>
 * - order-success: /sucessfullOrder/<payment-intent-id> (Stripe's return)
 * - giftcard-receipt: /giftcard/receipt/<gift-card-id>
 * and the remaining email-linked pages (plans/websitev2-parity-m5-remaining-pages.md):
 * - waiting-list-unsubscribe: /waiting-list/unsubscribe/<entry-id> (record-id)
 * - product-review: /product/purchase/<purchase-id>/review (record-id)
 * - submit-review: /submit-review (the school's review form; no id)
 * - reconciliation: /basket/reconciliation (ids in the page's query)
 * - bundle-auth: /auth/bundle/<bundle-id>?sig= (bundle-id; signs in, then
 *   on to /bundles/manage/<bundle-id>) */
export const BOOKING_VIEWS = [
  "manage",
  "cancel",
  "reschedule",
  "review",
  "bundle-dates",
  "bundle-manage",
  "flexitime-confirm",
  "flexitime-reschedule",
  "secure-payment",
  "secure-pay-booking",
  "request-proposal",
  "payment-success",
  "product-success",
  "order-success",
  "giftcard-receipt",
  "waiting-list-unsubscribe",
  "product-review",
  "submit-review",
  "reconciliation",
  "bundle-auth",
] as const;

/** The views keyed by `record-id` (the payment pages). */
export const RECORD_VIEWS = [
  "secure-payment",
  "secure-pay-booking",
  "request-proposal",
  "payment-success",
  "product-success",
  "order-success",
  "giftcard-receipt",
  "waiting-list-unsubscribe",
  "product-review",
] as const satisfies readonly BookingView[];

/** The views whose address names no record (their state is the school's,
 * or in the page's query). */
export const UNKEYED_VIEWS = ["submit-review", "reconciliation"] as const satisfies readonly BookingView[];

export type BookingView = (typeof BOOKING_VIEWS)[number];

export const isBookingView = (value: unknown): value is BookingView =>
  typeof value === "string" && (BOOKING_VIEWS as readonly string[]).includes(value);

export type BookingProps = {
  businessId?: string;
  businessSlug?: string;
  apiUrl?: string;
  /** Where its links go. Left out: the page's own site (relative links),
   * which is where these pages are. */
  widgetBaseUrl?: string;
  /** The page's own query to keep on every link, no leading "?" (the
   * website app's "schoolSlug=clay&theme=x" on preview hosts). */
  linkQuery?: string;
  /** Where a booking made on the marketplace is managed instead. */
  marketplaceUrl?: string;
  theme?: string;
  primaryColor?: string;
  view?: string;
  /** The booking (every view but bundle-manage). */
  bookingId?: string;
  /** The bundle (bundle-manage, bundle-auth). */
  bundleId?: string;
  /** The email link's verb (the flexitime views). */
  verb?: string;
  /** The email link's date, unix seconds (flexitime-reschedule). */
  date?: string;
  /** The payment pages' record: an order, payment, proposal, booking,
   * purchase, payment intent or gift card (RECORD_VIEWS). */
  recordId?: string;
  /** A payment link's instalment, when the address names one
   * (secure-payment). */
  instalment?: string;
};

/** What every view is given: where to fetch, where to link. */
export type ViewContext = {
  /** The API root, no trailing slash ("https://api…"; paths add /widget/…). */
  api: string;
  businessId: string;
  businessSlug: string;
  links: import("./links").BookingLinks;
  /** The school's colour (the sign-in dialog's). */
  primaryColor?: string;
  /** Sends a marketplace booking where it is managed. */
  toMarketplace: (path: string) => void;
};
